import React, { useRef } from "react";
import PropTypes from "prop-types";
import MDBox from "shared/ui/Box/MDBox";
import MDTypography from "shared/ui/Typography/MDTypography";
import Card from "@mui/material/Card";
import useDeviceType from "hooks/useDeviceType";
import DayLabelInput from "./DayLabelInput";

import usePlanner from "features/planner/state/usePlanner";
import DroppableSlot from "features/planner/components/dnd/DroppableSlot";
import DraggableExerciseSlotItem from "features/planner/components/dnd/DraggableExerciseSlotItem";
import EditModeDaySelector from "features/planner/components/editmode/EditModeDaySelector";
import { alpha } from "@mui/material/styles";

function DayColumn({ day, onEditSlot }) {
  const { isLaptop, isMedium, isLarge, isXL } = useDeviceType();
  const { programState, isEditMode, toggleSlotSelection, selectedSlots, selectWholeDay } =
    usePlanner();

  const dayMapping = {
    "Día 1:": "day1",
    "Día 2:": "day2",
    "Día 3:": "day3",
    "Día 4:": "day4",
    "Día 5:": "day5",
    "Día 6:": "day6",
    "Día 7:": "day7",
  };

  const plannerKey = dayMapping[day];
  const slots = programState.days[plannerKey].slots;

  const SLOTS = slots.length;
  const maxLines = isXL || isLarge ? 3 : isMedium || isLaptop ? 2 : 1;
  const fontSize = isXL ? "0.80rem" : isLarge ? "0.78rem" : isMedium ? "0.74rem" : "0.70rem";
  const isCompact = isLaptop || isMedium;

  const slotRefs = useRef([]);

  if (!slotRefs.current.length) {
    slotRefs.current = Array.from({ length: SLOTS }, () => React.createRef());
  }

  const filledSlotsCount = slots.filter(Boolean).length;

  return (
    <Card
      sx={{
        p: isLaptop ? "0px 6px 8px 6px" : "6px 10px 10px 10px",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "transparent",
        boxShadow: "none",
        border: "none",
      }}
    >
      <MDBox
        sx={{
          mb: isLaptop ? 0.2 : 0.5,
          height: isLaptop ? "26px" : "32px",
          display: "flex",
          alignItems: "center",
        }}
      >
        {!isEditMode ? (
          <DayLabelInput dayKey={plannerKey} defaultLabel={day} />
        ) : (
          <EditModeDaySelector
            onSelectDay={() => {
              if (filledSlotsCount > 0) {
                selectWholeDay(plannerKey);
              }
            }}
          />
        )}
      </MDBox>

      <MDBox
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          justifyContent: "space-between",
          mt: 0.3,
        }}
      >
        {Array.from({ length: SLOTS }).map((_, index) => {
          const slotId = `${plannerKey}-${index}`;
          const slot = slots[index];
          const isFilled = slot !== null;
          const isSelected = selectedSlots.some(
            (selection) => selection.dayKey === plannerKey && selection.slotIndex === index
          );

          return (
            <MDBox
              key={slotId}
              onClick={() => {
                if (isEditMode && isFilled) {
                  toggleSlotSelection(plannerKey, index);
                }
              }}
              sx={{
                width: "100%",
                height: `calc((100% - (${SLOTS - 1} * ${isLaptop ? 8 : 12}px)) / ${SLOTS})`,

                border: isSelected ? "2px solid" : isFilled ? "1px solid" : "1px dashed",
                borderColor: isSelected ? "primary.main" : isFilled ? "grey.400" : "grey.300",

                borderRadius: "8px",

                backgroundColor: isSelected
                  ? (theme) => alpha(theme.palette.primary.main, 0.08)
                  : isFilled
                  ? "background.paper"
                  : "grey.100",

                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: isEditMode ? "pointer" : "default",

                "&:hover": {
                  backgroundColor: (theme) =>
                    alpha(theme.palette.primary.main, isSelected ? 0.14 : 0.06),
                  borderColor: "primary.main",
                  transition: "all 0.15s ease-in-out",
                },

                overflow: "hidden",
              }}
            >
              <DroppableSlot
                id={slotId}
                day={plannerKey}
                index={index}
                ref={slotRefs.current[index]}
              >
                {isFilled ? (
                  <DraggableExerciseSlotItem
                    exercise={slot}
                    maxLines={maxLines}
                    fontSize={fontSize}
                    compact={isCompact}
                    day={plannerKey}
                    index={index}
                    onEdit={() => {
                      if (!isEditMode) {
                        onEditSlot(plannerKey, index, slotRefs.current[index].current);
                      }
                    }}
                  />
                ) : (
                  <MDTypography
                    sx={{
                      fontSize: "0.75rem",
                      color: "grey.400",
                      letterSpacing: "0.5px",
                      userSelect: "none",
                    }}
                  >
                    —
                  </MDTypography>
                )}
              </DroppableSlot>
            </MDBox>
          );
        })}
      </MDBox>
    </Card>
  );
}

DayColumn.propTypes = {
  day: PropTypes.string.isRequired,
  onEditSlot: PropTypes.func.isRequired,
};

export default DayColumn;
